import {skipToken} from '@reduxjs/toolkit/query';
import {Alert, Button, Card, showNotification, Switch} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {
  TelephonyLine,
  useApproveTelephonyLineRecordingConsentMutation,
  useGetTelephonyRecordingSettingsQuery,
  useRevokeTelephonyLineRecordingConsentMutation,
} from '@dms/api/settings';
import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {getUserName, handleApiError} from '@dms/shared';

import {Nullish, parseDate, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {RECORDING_SETTINGS_REQUIRED_ERROR_CODE} from '../../../constants/RECORDING_SETTINGS_REQUIRED_ERROR_CODE';
import {RECORDING_SETTINGS_SECTION_ID} from '../../../constants/RECORDING_SETTINGS_SECTION_ID';

interface TelephonyLineRecordingConsentCardProps extends RequiredTestIdProps {
  line: TelephonyLine | Nullish;
  isReadOnly: boolean;
}

export function TelephonyLineRecordingConsentCard(props: TelephonyLineRecordingConsentCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();

  const [approveConsent, {isLoading: isApproving}] =
    useApproveTelephonyLineRecordingConsentMutation();
  const [revokeConsent, {isLoading: isRevoking}] = useRevokeTelephonyLineRecordingConsentMutation();
  const {data: recordingSettings} = useGetTelephonyRecordingSettingsQuery();

  const consent = props.line?.recordingConsent;

  const {data: approver} = useGetUserQuery(
    isNotNil(consent?.approvedBy) ? {id: consent.approvedBy} : skipToken
  );

  const line = props.line;

  if (isNil(line) || isNil(consent)) {
    return null;
  }

  // Recording is refused until the tenant saved its recording settings once; revoking never is.
  const isBlockedByRecordingSettings =
    !consent.approved && isNotNil(recordingSettings) && isNil(recordingSettings.changedBy);

  const handleChange = (isApproved: boolean) => {
    const mutation = isApproved ? approveConsent : revokeConsent;

    mutation({lineId: line.id})
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch((error: ApiError) => {
        const isRecordingSettingsRequired = error.data?.errors?.some(
          (item) => item.code === RECORDING_SETTINGS_REQUIRED_ERROR_CODE
        );

        if (isRecordingSettingsRequired) {
          showNotification.error(i18n.t('page.settings.telephony.recordingSettingsRequired'));
          return;
        }

        handleApiError(error);
      });
  };

  const approvedAt = parseDate(consent.approvedAt);
  const approvedLabel = [
    isNotNil(approvedAt)
      ? i18n.t('page.settings.telephony.recordingApprovedAt', {
          date: formatDateTime('dateTimeShort', approvedAt),
        })
      : null,
    getUserName(approver),
  ]
    .filter(isNotNil)
    .join(' · ');

  return (
    <Card
      title={i18n.t('page.settings.telephony.recordingCardTitle')}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <Text color="secondary" size="small">
          {i18n.t('page.settings.telephony.recordingDescription')}
        </Text>
        <Switch
          value={consent.approved}
          onChange={handleChange}
          label={i18n.t('page.settings.telephony.recordingSwitch')}
          isDisabled={props.isReadOnly || isBlockedByRecordingSettings}
          isLoading={isApproving || isRevoking}
          data-testid={suffixTestId('switch', props)}
        />
        <Show when={isBlockedByRecordingSettings}>
          <Alert
            type="inline"
            variant="warning"
            message={i18n.t('page.settings.telephony.recordingSettingsRequired')}
            data-testid={suffixTestId('recordingSettingsRequired', props)}
          />
        </Show>
        <Show when={consent.approved}>
          <Text color="secondary" size="xSmall" data-testid={suffixTestId('approvedAt', props)}>
            {approvedLabel}
          </Text>
        </Show>
        <HStack spacing={1} align="center" wrap>
          <Text color="secondary" size="small">
            {i18n.t('page.settings.telephony.recordingSettingsLinkLabel')}
          </Text>
          <Button
            variant="link"
            size="small"
            rightIcon="navigation/arrow_forward"
            title={i18n.t('page.settings.telephony.recordingSettingsLink')}
            onClick={() => navigate(`${settingsRoutes.telephony}#${RECORDING_SETTINGS_SECTION_ID}`)}
            data-testid={suffixTestId('recordingSettingsLink', props)}
          />
        </HStack>
      </VStack>
    </Card>
  );
}
